import { useEffect, useRef, useSyncExternalStore, type ComponentProps, type ReactNode } from 'react'
import { fileUrl, initials, pct, short } from '../lib/format'

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ')

export function Icon({ name, className = 'size-5' }: { name: string; className?: string }) {
  return <span aria-hidden className={cx('icon', className)} style={{ ['--icon' as string]: `url(/icons/${name}.svg)` }} />
}

// One page header for every console page: title, optional subtitle, actions on the right.
export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="truncate text-xl font-semibold text-ink">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-2">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function Card({ className, ...p }: ComponentProps<'div'>) {
  return <div className={cx('rounded-card bg-card', className)} {...p} />
}

// Every kg figure in a stat card renders the same way: compact number, then a smaller " kg".
export const kgStat = (n: number) => <>{short(n)}<span className="text-base font-medium"> kg</span></>

export function Trend({ value }: { value: number }) {
  return (
    <span className="num inline-flex items-center gap-1 text-xs whitespace-nowrap">
      {pct(value)}
      <Icon name={value >= 0 ? 'arrow-rise' : 'arrow-fall'} className="size-4" />
    </span>
  )
}

type Tone = 'peach' | 'stone' | 'card'
const tones: Record<Tone, string> = { peach: 'bg-peach', stone: 'bg-stone', card: 'bg-card' }

export function StatCard({ label, value, sub, change, tone = 'card', big }: { label: ReactNode; value: ReactNode; sub?: ReactNode; change?: number; tone?: Tone; big?: boolean }) {
  return (
    <div className={cx('min-w-0 rounded-card px-6 pt-6 pb-5', tones[tone])}>
      <p className="text-sm leading-5 font-semibold">{label}</p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3">
        <p className={cx('num font-semibold leading-9 whitespace-nowrap', big ? 'text-2xl' : 'text-xl')}>{value}</p>
        {change != null && <Trend value={change} />}
      </div>
      <p className="num h-3 text-[10px] leading-3 text-ink-3/70">{sub}</p>
    </div>
  )
}

export function Avatar({ name, photoKey, className = 'size-6 text-[10px]' }: { name: string; photoKey?: string | null; className?: string }) {
  const src = fileUrl(photoKey)
  return src ? (
    <img src={src} alt="" className={cx('flex-none rounded-full object-cover', className)} loading="lazy" />
  ) : (
    <span aria-hidden className={cx('grid flex-none place-items-center rounded-full bg-peach-soft font-semibold text-brand-dark', className)}>{initials(name)}</span>
  )
}

export function Tag({ children }: { children: ReactNode }) {
  return <span className="inline-block rounded-md bg-brand px-1.5 text-[8px] font-semibold leading-4 whitespace-nowrap text-white">{children}</span>
}

// The cylinder outline from the design, filled green/amber/red by how much gas is left.
export function Cylinder({ level, className = 'size-5' }: { level: string; className?: string }) {
  const color = level === 'full' ? 'text-good-2' : level === 'mid' ? 'text-[#e8a33d]' : 'text-brand'
  return (
    <span className={cx('relative inline-block', className)}>
      <span className="icon absolute inset-0 text-ink" style={{ ['--icon' as string]: 'url(/img/cylinder-lg.webp)' }} />
      <span className={cx('absolute inset-x-[27%] bottom-[16%] rounded-[1px] bg-current', color, level === 'full' ? 'top-[45%]' : level === 'mid' ? 'top-[62%]' : 'top-[74%]')} />
    </span>
  )
}

export const levelWord = (l: string) => (l === 'full' ? 'Full' : l === 'mid' ? 'Mid' : 'Low')

export function Button({ variant = 'primary', className, ...p }: ComponentProps<'button'> & { variant?: 'primary' | 'dark' | 'ghost' | 'light' }) {
  const v = {
    primary: 'bg-brand text-white hover:bg-brand-dark',
    dark: 'bg-ink text-white hover:bg-black',
    ghost: 'text-ink hover:bg-black/5',
    light: 'bg-stone/70 text-ink hover:bg-stone',
  }[variant]
  return <button className={cx('inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition-colors disabled:opacity-50', v, className)} {...p} />
}

export function Field({ label, error, hint, children, className }: { label: ReactNode; error?: string | null; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cx('block', className)}>
      <span className="mb-1.5 block text-xs font-medium text-ink-2">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-muted">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-danger">{error}</span>}
    </label>
  )
}

export const inputCls = 'h-11 w-full rounded-lg border border-line bg-white px-3 text-sm placeholder:text-faint focus:border-brand focus:outline-none'

export function Input(p: ComponentProps<'input'>) {
  return <input {...p} className={cx(inputCls, p.className)} />
}

export function Select({ className, ...p }: ComponentProps<'select'>) {
  return <select {...p} className={cx(inputCls, 'appearance-none bg-[url(/icons/chevron-down.svg)] bg-[length:20px] bg-[right_8px_center] bg-no-repeat pr-9', className)} />
}

export function Spinner({ className = 'size-5' }: { className?: string }) {
  return <span className={cx('inline-block animate-spin rounded-full border-2 border-current border-r-transparent', className)} role="status" aria-label="Loading" />
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-lg bg-black/5', className)} />
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="rounded-card border border-danger/20 bg-white p-5 text-sm">
      <p className="font-semibold text-danger">Couldn't load this</p>
      <p className="mt-1 text-ink-2">{error instanceof Error ? error.message : String(error)}</p>
      {onRetry && <Button variant="light" className="mt-3 h-8" onClick={onRetry}>Try again</Button>}
    </div>
  )
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="px-6 py-12 text-center">
      <p className="font-semibold">{title}</p>
      {children && <div className="mt-1 text-sm text-ink-2">{children}</div>}
    </div>
  )
}

// Native <dialog>: focus trap, Esc and top-layer for free.
export function Modal({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean | 'xl' | 'full' }) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
  }, [open])
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className={cx('m-auto w-[calc(100%-32px)] rounded-card bg-white p-0 text-ink shadow-card', wide === 'full' ? 'max-w-[1600px]' : wide === 'xl' ? 'max-w-5xl' : wide ? 'max-w-2xl' : 'max-w-md')}
    >
      {open && (
        <div className="p-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-lg font-bold">{title}</h2>
            <button onClick={onClose} className="grid size-8 place-items-center rounded-lg text-xl leading-none text-muted hover:bg-black/5" aria-label="Close">×</button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  )
}

export function Pagination({ page, total, pageSize, onPage }: { page: number; total: number; pageSize: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  if (pages <= 1) return null
  const start = Math.max(1, Math.min(page - 2, pages - 4))
  const nums = Array.from({ length: Math.min(5, pages) }, (_, i) => start + i)
  const btn = 'grid size-7 place-items-center rounded-lg text-xs disabled:opacity-30'
  return (
    <nav className="flex items-center justify-end gap-2 py-4" aria-label="Pagination">
      <button className={btn} disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page"><Icon name="arrow-left" className="size-4" /></button>
      {nums.map((n) => (
        <button key={n} className={cx(btn, n === page ? 'bg-black/5 font-medium' : 'hover:bg-black/5')} onClick={() => onPage(n)} aria-current={n === page ? 'page' : undefined}>{n}</button>
      ))}
      <button className={btn} disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page"><Icon name="arrow-right" className="size-4" /></button>
    </nav>
  )
}

export const PERIODS = [
  ['today', 'Today'], ['week', 'This week'], ['month', 'This month'], ['quarter', 'This quarter'], ['year', 'This year'],
] as const

export function PeriodSelect({ value, onChange }: { value: string; onChange: (v: (typeof PERIODS)[number][0]) => void }) {
  return (
    <label className="relative inline-flex h-10 items-center gap-1 rounded-lg border border-line bg-white pr-2 pl-3 text-sm font-semibold hover:border-black/20">
      {PERIODS.find(([k]) => k === value)?.[1]}
      <Icon name="chevron-down" className="size-5 text-muted" />
      <select aria-label="Period" className="absolute inset-0 cursor-pointer opacity-0" value={value} onChange={(e) => onChange(e.target.value as never)}>
        {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </label>
  )
}

// Minimal toast store; one line of feedback after an action.
let toasts: { id: number; text: string; tone: 'ok' | 'error' }[] = []
const subs = new Set<() => void>()
export function toast(text: string, tone: 'ok' | 'error' = 'ok') {
  const id = Date.now() + Math.random()
  toasts = [...toasts, { id, text, tone }]
  subs.forEach((s) => s())
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id)
    subs.forEach((s) => s())
  }, 4000)
}
export function Toaster() {
  const list = useSyncExternalStore((s) => (subs.add(s), () => subs.delete(s)), () => toasts)
  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col items-center gap-2 sm:items-end" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={cx('pointer-events-auto max-w-sm rounded-xl px-4 py-3 text-sm font-medium text-white shadow-card', t.tone === 'ok' ? 'bg-ink' : 'bg-danger')}>{t.text}</div>
      ))}
    </div>
  )
}

export function Menu({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div className="absolute top-8 left-0 z-20 w-44 rounded-xl bg-white p-1.5 shadow-card">{children}</div>
    </>
  )
}

export function MenuItem({ children, onClick, active }: { children: ReactNode; onClick: () => void; active?: boolean }) {
  return <button onClick={onClick} className={cx('block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-page', active && 'font-semibold text-brand')}>{children}</button>
}

export function useMedia(query: string) {
  return useSyncExternalStore(
    (cb) => {
      const m = matchMedia(query)
      m.addEventListener('change', cb)
      return () => m.removeEventListener('change', cb)
    },
    () => matchMedia(query).matches,
  )
}
